import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy } from 'lucide-react';
import { API, absoluteImageUrl } from '../lib/api';
import { networkOf } from '../lib/networks';
import { shortWei } from '../lib/format';

const RANK_STYLES = [
  'bg-[#FBBF24]/15 border-[#FBBF24]/40 text-[#FBBF24]',   // gold
  'bg-[#D1D5DB]/15 border-[#D1D5DB]/40 text-[#D1D5DB]',   // silver
  'bg-[#F59E0B]/10 border-[#B45309]/40 text-[#D97706]',   // bronze
];

const Leaderboard = () => {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/leaderboard?limit=5`)
      .then(({ data }) => { if (!cancelled) setEntries(data.entries || []); })
      .catch(() => { if (!cancelled) setEntries([]); });
    return () => { cancelled = true; };
  }, []);

  if (!entries || entries.length === 0) return null;

  return (
    <div className="cf-card p-5 sm:p-6 mt-8" data-testid="leaderboard">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FBBF24]/25 to-[#F472B6]/15 border border-[#FBBF24]/30 flex items-center justify-center">
          <Trophy className="w-4.5 h-4.5 text-[#FBBF24]" />
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold leading-tight">Top earners</h2>
          <p className="text-xs text-forge-muted">Ranked by realized platform revenue — real on-chain balances</p>
        </div>
      </div>
      <div className="space-y-2">
        {entries.map((e, i) => {
          const img = absoluteImageUrl(e.image_url);
          const net = networkOf(e.chain_id);
          const earned = BigInt(e.realized_wei || '0');
          return (
            <Link
              key={`${e.contract_address}-${e.chain_id}`}
              to={`/token/${e.contract_address}?chain=${e.chain_id}`}
              data-testid={`leaderboard-row-${i + 1}`}
              className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] hover:border-[#7DF9FF]/25 transition-colors no-underline text-white"
            >
              <span className={`w-8 h-8 rounded-full border flex items-center justify-center font-display font-bold text-sm shrink-0 ${RANK_STYLES[i] || 'bg-white/[0.04] border-white/10 text-forge-muted'}`}>
                {i + 1}
              </span>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7DF9FF]/15 to-[#A78BFA]/15 border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                {img ? <img src={img} alt={e.name} className="w-full h-full object-cover" />
                     : <span className="font-display font-bold cf-gradient-text">{e.symbol?.[0] || '?'}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display font-semibold truncate text-sm">{e.name}</p>
                <p className="text-xs text-forge-muted">${e.symbol} <span className="cf-chip !py-0 !px-1.5 !text-[9px] ml-1">{net.short}</span></p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-display font-bold text-sm">
                  {earned > 0n ? shortWei(e.realized_wei) : '0'} <span className="text-forge-muted font-normal text-xs">${e.symbol}</span>
                </p>
                <p className="text-[10px] text-forge-muted">fees routed to platform</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Leaderboard;
